import { useMutation, useQueryClient } from '@tanstack/react-query';
import { track } from '@citibox-panel/analytics';
import type { UserUpdate } from '@citibox-panel/api-client';

// The «Editar datos» WRITE — the panel's ninth, and the second over the «Usuario» entity.
// retry: false, like every write here: an irreversible act must never be silently re-sent.
//
// On success, in this order:
//  1. The categorical analytics event (three flags, zero PII — see the security case in the test).
//  2. The account read invalidated AND AWAITED. Returning the promise keeps the mutation pending
//     until the refetch lands, the same deliberate inversion the baja makes: `UserLayout` only
//     skeletons on `isPending`, so leaving edit mode early would flash the pre-write values.
//  3. `onRenamed`, AFTER that refetch settles — the tab's label is the titular's name, and
//     relabelling it before the card behind it agrees leaves the two disagreeing on screen.
//  4. The three reads that EMBED the titular's name, fire-and-forget: the form must not stay
//     locked waiting for screens the agent is not looking at.
//
// On error, nothing is invalidated — the one divergence from `useDeleteUser`, which does refetch
// code-agnostically. `useAccount` carries `throwOnError: true`, so an error refetch that itself
// failed would send the whole ficha to its `FeatureErrorBoundary` and take the form the agent has
// to retry from with it. The baja could afford that because its modal died either way; here the
// half-filled form IS what has to survive.
export function useUpdateUser(
  userId: string,
  phoneKey: string,
  updateUser: (id: string, changes: UserUpdate) => Promise<void>,
  onRenamed?: (name: string) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: UserUpdate) => updateUser(userId, changes),
    retry: false,
    onSuccess: async (_data, changes) => {
      track('user_updated', {
        name_changed: changes.name !== undefined ? 'true' : 'false',
        email_changed: changes.email !== undefined ? 'true' : 'false',
        address_changed: changes.locationId !== undefined ? 'true' : 'false',
      });

      await queryClient.invalidateQueries({ queryKey: ['phone-search', phoneKey] });

      // Read off `changes` and not off the refetched account: what the agent asked for is what
      // names the tab, and it is already the value the awaited read just brought back.
      if (changes.name !== undefined) {
        onRenamed?.(changes.name);
      }

      // The three reads that cache the titular's name inside a delivery row. Never awaited, and
      // key-PREFIX rather than key-exact: every delivery detail, every building's list and every
      // tracking answer is stale, not just the ones this account appears in.
      void queryClient.invalidateQueries({ queryKey: ['delivery-detail'] });
      void queryClient.invalidateQueries({ queryKey: ['location-deliveries'] });
      void queryClient.invalidateQueries({ queryKey: ['tracking-search'] });
    },
  });
}
